import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Copy } from "lucide-react";

export default function Typologies() {
  const [typologies, setTypologies] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, libelle, composition:[]}

  const load = async () => {
    const [t, p] = await Promise.all([api.get("/typologies"), api.get("/catalogue/pieces")]);
    setTypologies(t.data);
    setPieces(p.data);
  };
  useEffect(() => { load(); }, []);

  const pieceLabel = (code) => pieces.find((p) => p.code === code)?.libelle || code;

  const startNew = () => setEditing({ libelle: "", ordre: typologies.length + 1, composition: [] });

  const save = async () => {
    try {
      const payload = { libelle: editing.libelle, ordre: editing.ordre || 0, composition: editing.composition };
      if (editing.id) await api.put(`/typologies/${editing.id}`, payload);
      else await api.post("/typologies", payload);
      setEditing(null);
      load();
      toast.success("Typologie enregistrée");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`/typologies/${id}`); load(); };

  const duplicate = (t) => setEditing({
    libelle: `Copie de ${t.libelle}`, ordre: typologies.length + 1,
    composition: (t.composition || []).map((c) => ({ ...c })),
  });

  const addComp = () => setEditing((e) => ({ ...e, composition: [...e.composition, { type_piece: pieces[0]?.code || "", count: 1 }] }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dd-blue mb-1">Typologies</h1>
          <p className="text-sm text-dd-warm">Compositions modifiables. Un point de départ, pas une règle juridique.</p>
        </div>
        <Button onClick={startNew} data-testid="new-typologie-btn" className="h-11 bg-dd-blue hover:bg-dd-night text-white">
          <Plus size={16} className="mr-1" /> Nouvelle typologie
        </Button>
      </div>

      <div className="grid gap-4">
        {typologies.map((t) => (
          <div key={t.id} className="bg-white rounded-lg border border-dd-light p-5" data-testid={`typologie-${t.id}`}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-dd-blue">{t.libelle}</h3>
              <div className="flex gap-3">
                <button onClick={() => setEditing({ ...t })} className="text-sm text-dd-blue" data-testid={`typologie-edit-${t.id}`}>Modifier</button>
                <button onClick={() => duplicate(t)} className="text-sm text-dd-blue flex items-center gap-1" data-testid={`typologie-duplicate-${t.id}`}><Copy size={14} /> Dupliquer</button>
                <button onClick={() => del(t.id)} className="text-destructive"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(t.composition || []).map((c, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-full bg-dd-light text-dd-night">
                  {pieceLabel(c.type_piece)}{c.count > 1 ? ` ×${c.count}` : ""}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-dd-night/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-dd-blue mb-4">{editing.id ? "Modifier" : "Nouvelle"} typologie</h3>
            <Input value={editing.libelle} onChange={(e) => setEditing({ ...editing, libelle: e.target.value })}
              placeholder="Libellé (ex. 3.5 pièces)" className="h-11 mb-4" data-testid="typologie-libelle" />
            <div className="space-y-2">
              {editing.composition.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={c.type_piece} onValueChange={(v) => setEditing((e) => ({ ...e, composition: e.composition.map((x, j) => j === i ? { ...x, type_piece: v } : x) }))}>
                    <SelectTrigger className="h-11 flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{pieces.map((p) => <SelectItem key={p.code} value={p.code}>{p.libelle}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min={1} value={c.count} className="h-11 w-20"
                    onChange={(e) => setEditing((ed) => ({ ...ed, composition: ed.composition.map((x, j) => j === i ? { ...x, count: parseInt(e.target.value) || 1 } : x) }))} />
                  <button onClick={() => setEditing((e) => ({ ...e, composition: e.composition.filter((_, j) => j !== i) }))} className="text-destructive"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <button onClick={addComp} className="text-sm text-dd-blue flex items-center gap-1.5 mt-3"><Plus size={15} /> Ajouter une pièce</button>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setEditing(null)} className="h-11 border-dd-light">Annuler</Button>
              <Button onClick={save} data-testid="typologie-save" className="h-11 bg-dd-blue hover:bg-dd-night text-white">Enregistrer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
